import React, { useCallback, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { DollarOutlined } from '@ant-design/icons';
import { Button, Spin, Tooltip } from 'antd';

import Transaction from 'components/Transaction';
import TransferForeign from 'components/TransferForeign';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { Space } from 'v2/components/Shared/Space';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import type { FeatureAccessStatus } from 'v2/interfaces/featureAccess';
import { useUserStore } from 'v2/store/User';

import { useBalance } from './useBalance';

interface WalletProps {
  anticipationStatus: FeatureAccessStatus;
}

const Wallet: React.FC<WalletProps> = ({ anticipationStatus }) => {
  const [showModalTransaction, setShowModalTransaction] = useState(false);
  const [showModalForeign] = useState(false);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const { data: balance, isFetching: balanceIsLoading, refetch: refetchBalance } = useBalance();

  const { currentUser, currentUserIsFetching } = useUserStore(({ user, isLoading }) => ({
    currentUser: user,
    currentUserIsFetching: isLoading
  }));
  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const handleModal = useCallback(() => {
    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    setShowModalTransaction(prev => !prev);
  }, [shouldBlock]);

  const handleCloseModal = useCallback(() => {
    setShowModalTransaction(false);
  }, []);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    setShowModalTransaction(prev => !prev);
  }, [dismissBlock]);

  const getBlockMessage = useCallback((hasBlockTransferBeta: boolean, status: FeatureAccessStatus) => {
    if (status?.status === 'blocked') {
      return status.reason;
    }

    if (hasBlockTransferBeta) {
      return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    }

    return '';
  }, []);

  const currency = 'BRL';
  const totalValue = balance?.total_value;
  const valor = balance?.value;
  const anticipationValue = balance?.available_antecipation;

  const showTotalValue = !balanceIsLoading && typeof totalValue === 'number';
  const showValue = !balanceIsLoading && typeof valor === 'number';
  const showAnticipationValue = !balanceIsLoading && typeof anticipationValue === 'number';
  const shouldBlockTransferBtn = balanceIsLoading || anticipationStatus?.status === 'blocked';

  return (
    <>
      <div className='h-full rounded bg-white shadow'>
        <div className='flex h-full flex-col justify-between p-4'>
          <div className='flex flex-wrap justify-between'>
            <h4 className='text-lg font-semibold'>Wallet ({currency})</h4>

            <div className='w-full'>
              <p className='m-0! text-right text-base'>Saldo total:</p>
              <p className='m-0! block text-right text-xs'>Soma do saldo disponível com o saldo futuro</p>

              <h5 className='m-0 text-right text-xl font-semibold'>
                {balanceIsLoading && <Spin size='small' />}
                {showTotalValue && (
                  <Mask>
                    <FormattedNumber format={currency} value={totalValue} />
                  </Mask>
                )}
              </h5>
              <Space size={8} axis='vertical' />
              <p className='m-0! block text-right text-base'>Saldo disponível para transferência:</p>
              <p className='m-0! block text-right text-xs'>Total de ganhos que já estão disponíveis para transferir</p>

              <h5 className='m-0 text-right text-xl font-semibold'>
                {balanceIsLoading && <Spin size='small' />}
                {showValue && (
                  <Mask>
                    <FormattedNumber format={currency} value={valor} />
                  </Mask>
                )}
              </h5>
              <Space size={8} axis='vertical' />
              <p className='m-0! text-right text-base'>Saldo a antecipar:</p>
              <p className='m-0! block text-right text-xs'>Ganhos que podem ser antecipados</p>

              <h5 className='m-0 text-right text-xl font-semibold'>
                {balanceIsLoading && <Spin size='small' />}
                {showAnticipationValue && (
                  <Mask>
                    <FormattedNumber format={currency} value={balance?.available_antecipation} />
                  </Mask>
                )}
              </h5>
              <Space size={16} axis='vertical' />
            </div>
          </div>

          <div className='flex items-end justify-between'>
            <PermissionHide action='myeduzz:dashboard:solicitar-transferencia'>
              <a
                style={{ fontSize: 14 }}
                href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
                target='_blank'
                rel='noopener noreferrer'
              >
                Entenda a antecipação
              </a>

              <WithBetaModules>
                {(betaModules: string[]) => {
                  if (!currentUserIsFetching && !currentUser?.has_transfer) {
                    const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
                    const blockBtn = shouldBlockTransferBtn || userHasBlockTransferBetaModule;
                    const blockMessage = getBlockMessage(userHasBlockTransferBetaModule, anticipationStatus);

                    return (
                      <Tooltip title={blockBtn ? blockMessage : ''}>
                        <div>
                          <Button
                            id='myTransferButton'
                            className='disabled:bg-grey-100! bg-[#009358]! text-white! hover:bg-[#007A49]! max-sm:float-none!'
                            disabled={blockBtn}
                            onClick={handleModal}
                            icon={<DollarOutlined />}
                          >
                            Transferir agora
                          </Button>
                        </div>
                      </Tooltip>
                    );
                  }

                  return null;
                }}
              </WithBetaModules>
              {currentUser?.has_transfer && <div>Você tem transferências em processamento</div>}
            </PermissionHide>
          </div>
        </div>
      </div>

      {showModalTransaction && (
        <Transaction
          selfClose={handleCloseModal}
          successCallback={refetchBalance}
          transferByInvoice={false}
          newAntecipationTransfer={false}
          open={showModalTransaction}
          onCloseModalTransfer={handleCloseModal}
        />
      )}

      {showModalForeign && (
        <TransferForeign
          value={valor}
          selfClose={handleModal}
          successCallback={refetchBalance}
          open={showModalForeign}
          onCloseModalTransfer={handleModal}
        />
      )}

      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />
    </>
  );
};

export default Wallet;
